import { Box, Typography } from '@mui/material';
import React, { useRef } from 'react';
import zeplinColor from 'theme/Color';
import { LightTooltip } from 'components/shared/CustomTooltip/CustomTooltip';
import style from 'components/shared/CustomViewTask/CustomViewTask.module.css';

interface LeadAttachmentsProps {
  files: (File | string)[];
  onFileSelect?: (files: File[]) => void;
  onFileDelete?: (index: number) => void;
}

export const LeadAttachments: React.FC<LeadAttachmentsProps> = ({
  files,
  onFileSelect,
  onFileDelete,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileInputClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && onFileSelect) {
      const selectedFiles = Array.from(event.target.files);
      onFileSelect(selectedFiles);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const getFileIcon = (file: File | string) => {
    const extension =
      file instanceof File
        ? file.name.split('.').pop()?.toLowerCase()
        : file.toString().split('.').pop()?.toLowerCase();
    return `/assets/${extension}.svg`;
  };

  const getFileName = (file: File | string) => {
    if (file instanceof File) return file.name;

    const filePath = file.toString() || '';
    if (filePath.includes('|')) {
      const s3Key = filePath.split('|')[1];
      const parts = s3Key.split('/');
      return parts[parts.length - 1] || s3Key;
    }
    const parts = filePath.split('/');
    return parts[parts.length - 1] || filePath;
  };

  const handleDownload = async (file: File | string) => {
    if (file instanceof File) {
      const url = URL.createObjectURL(file);
      const link = document.createElement('a');
      link.href = url;
      link.download = file.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      return;
    }

    const filePath = file.toString();
    const fileUrl = filePath.includes('|')
      ? `${filePath.split('|')[0]}${filePath.split('|')[1]}`
      : filePath.startsWith('http')
        ? filePath
        : `https://huddle-users-dev.s3.us-east-1.amazonaws.com/${filePath}`;

    try {
      const response = await fetch(fileUrl);
      if (!response.ok) throw new Error('Failed to fetch file');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = getFileName(file);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch {
      window.open(fileUrl, '_blank');
    }
  };

  const getFileSize = (file: File | string) =>
    file instanceof File && file.size ? `${file.size / 10000}MB` : '';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: { xs: '10px', xl: '16px' },
        width: '100%',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          width: '100%',
          alignItems: 'center',
        }}
      >
        <Typography variant='responsiveHeading3' sx={{ color: zeplinColor.BasicBlack }}>
          Attachment
        </Typography>

        {onFileSelect && (
          <Box
            sx={{ display: 'flex', gap: '8px', alignItems: 'center', cursor: 'pointer' }}
            onClick={handleFileInputClick}
          >
            <input
              ref={fileInputRef}
              type='file'
              multiple
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />
            <img
              width={'24px'}
              height={'24px'}
              src={'/assets/upload-blue.svg'}
              alt='upload'
              style={{ objectFit: 'fill' }}
            />
            <Typography variant='buttonMedium' color={zeplinColor.Brand1}>
              Select File
            </Typography>
          </Box>
        )}
      </Box>

      {files?.length > 0 ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: '10px', xl: '16px' } }}>
          {files.map((file, index) => {
            const fileName = getFileName(file);
            const fileIcon = getFileIcon(file);
            const fileSize = getFileSize(file);

            return (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '12px',
                }}
              >
                <Box sx={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                  <img
                    className={style['file-icon']}
                    src={fileIcon}
                    alt='file-type'
                    style={{ objectFit: 'fill' }}
                  />
                  <Box sx={{ display: 'flex', gap: '8px', flexDirection: 'column' }}>
                    <LightTooltip
                      title={
                        <Typography variant='body3' color={zeplinColor.Neutral1}>
                          {fileName}
                        </Typography>
                      }
                      placement='bottom-end'
                      style={{ backgroundColor: zeplinColor.BasicBlack, borderRadius: '8px' }}
                    >
                      <Typography variant='responsiveBody2'>
                        {fileName.slice(0, 40)}
                        {fileName.length > 40 && '...'}
                      </Typography>
                    </LightTooltip>
                    <Typography variant='responsiveLabel1'>{fileSize}</Typography>
                  </Box>
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    gap: '12px',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                  }}
                >
                  <img
                    className={style['icon']}
                    src={'/assets/download.svg'}
                    alt='download'
                    style={{ objectFit: 'fill', cursor: 'pointer' }}
                    onClick={() => handleDownload(file)}
                  />
                  {onFileDelete && (
                    <img
                      className={style['icon']}
                      src={'/assets/delete-blue.svg'}
                      alt='delete'
                      style={{ objectFit: 'fill', cursor: 'pointer' }}
                      onClick={() => onFileDelete(index)}
                    />
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      ) : (
        <Typography variant='responsiveBody3' color={zeplinColor.Neutral8} sx={{ padding: '12px' }}>
          No attachments
        </Typography>
      )}
    </Box>
  );
};
